import { Button, Grid } from '@material-ui/core'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { inviteCustomer } from '../../../actions/userActions'
import statesList from '../../../data/states'
import { useEmailValidator } from '../../../hooks/useEmailValidator'
import { useInput } from '../../../hooks/useInput'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import TextInput from '../../Form/TextInput'
import Message from '../../Message/Message'

interface Props {
  setShowAddCustomer: (val: boolean) => void
}

const AddCustomer = ({ setShowAddCustomer }: Props) => {
  const dispatch = useDispatch()

  const { error, loading } = useSelector(
    (state: RootStateOrAny) => state.userInvite
  )

  const {
    value: firstName,
    bind: bindFirstName,
    error: firstNameError,
    setError: setFirstNameError,
  } = useInput('')
  const {
    value: lastName,
    bind: bindLastName,
    error: lastNameError,
    setError: setLastNameError,
  } = useInput('')
  const {
    value: email,
    bind: bindEmail,
    error: emailError,
    setError: setEmailError,
  } = useInput('')
  const { value: phone, bind: bindPhone } = useInput('')
  const {
    value: companyName,
    bind: bindCompanyName,
    error: companyNameError,
  } = useInput('')
  const {
    value: streetAddress,
    bind: bindStreetAddress,
    error: streetAddressError,
  } = useInput('')
  const { value: aptSuiteOther, bind: bindAptSuiteOther } = useInput('')
  const {
    value: zipCode,
    bind: bindZipCode,
    error: zipCodeError,
  } = useInput('')
  const { value: city, bind: bindCity, error: cityError } = useInput('')
  const { value: state, setValue: setState, error: stateError } = useInput('')

  const emailIsValid = useEmailValidator(email as string)

  const formIsValid = () => {
    let ready = true

    if (firstName === '') {
      ready = false
      setFirstNameError('Please enter a first name')
    } else {
      setFirstNameError('')
    }

    if (lastName === '') {
      ready = false
      setLastNameError('Please enter a last name')
    } else {
      setLastNameError('')
    }

    if (email === '') {
      ready = false
      setEmailError('Please enter an email address')
    } else if (!emailIsValid) {
      ready = false
      setEmailError('Please enter a valid email')
    } else {
      setEmailError('')
    }

    return ready
  }

  const handleInviteUser = () => {
    if (formIsValid()) {
      const newUser = {
        firstName,
        lastName,
        email,
        phone,
        company: {
          companyName,
          streetAddress,
          aptSuiteOther,
          city,
          state,
          zipCode,
        },
      }
      dispatch(inviteCustomer(newUser))
    }
  }

  return (
    <div className="form-container">
      <h4>Customer information</h4>

      <Grid container spacing={2} className="m-t-16">
        <Grid item xs={6}>
          <TextInput
            name="firstName"
            label="First name"
            bindValue={bindFirstName}
            error={firstNameError}
          />
        </Grid>

        <Grid item xs={6}>
          <TextInput
            name="lastName"
            label="Last name"
            bindValue={bindLastName}
            error={lastNameError}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            name="email"
            label="Email"
            bindValue={bindEmail}
            error={emailError}
          />
        </Grid>

        <Grid item xs={6}>
          <TextInput name="phone" label="Phone number" bindValue={bindPhone} />
        </Grid>
      </Grid>

      <h4 className="m-t-40">Company information</h4>

      <Grid container spacing={4} className="m-b-60">
        <Grid item xs={12} sm={6} className="p-b-0">
          <TextInput
            name="companyName"
            label="Company name"
            bindValue={bindCompanyName}
            error={companyNameError}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="p-b-0"></Grid>
        <Grid item xs={12} sm={6} className="p-b-0">
          <TextInput
            name="streetAddress"
            label="Street address"
            bindValue={bindStreetAddress}
            error={streetAddressError}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="p-b-0">
          <TextInput
            name="aptSuiteOther"
            label="Apt/Suite/Other"
            bindValue={bindAptSuiteOther}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="p-b-0">
          <TextInput
            name="city"
            label="City"
            bindValue={bindCity}
            error={cityError}
          />
        </Grid>

        <Grid item xs={6} sm={2} className="p-b-0">
          <AutoCompleteSelect
            label="State"
            name="state"
            options={statesList}
            value={state as string}
            setValue={setState}
            error={stateError}
            minWidth={93}
          />
        </Grid>
        <Grid item xs={6} sm={4} className="p-b-0">
          <TextInput
            name="zipCode"
            label="Zip Code"
            bindValue={bindZipCode}
            error={zipCodeError}
          />
        </Grid>
      </Grid>

      <div className="flex flex-end m-t-30">
        <div>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowAddCustomer(false)}
            disabled={loading}
            className={`m-r-32 ${loading ? 'loading' : ''}`}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleInviteUser}
            disabled={loading}
            className={loading ? 'loading' : ''}
          >
            Add customer
          </Button>
        </div>
      </div>

      {error && <Message variant="error">{error}</Message>}
    </div>
  )
}

export default AddCustomer
